import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import { Dashboard, DashboardModal } from '@uppy/react';
import classNames from 'classnames';
import isArray from 'lodash-es/isArray';
import isObject from 'lodash-es/isObject';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { PropTypes as PropTypes$1 } from '@panneau/core';
import { useQuery } from '@panneau/core/hooks';
import Button from '@panneau/element-button';
import Label from '@panneau/element-label';
import MediaCard, { MediaCards } from '@panneau/element-media-card';
import ModalResourceItems from '@panneau/modal-resource-items';
import { useUppy } from '@panneau/uppy';
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';
import '@uppy/drag-drop/dist/style.css';
import '@uppy/status-bar/dist/style.css';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';

var styles = {"container":"panneau-field-upload-container","dashboard":"panneau-field-upload-dashboard","dashboardModal":"panneau-field-upload-dashboardModal","uppyDashboardContainer":"panneau-field-upload-uppyDashboardContainer"};

var propTypes$1 = {
  resource: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.shape({
    filename: PropTypes.string,
    size: PropTypes.number,
    url: PropTypes.string
  })]),
  name: PropTypes.string,
  types: PropTypes.arrayOf(PropTypes.oneOf(['audio', 'image', 'video', 'document'])),
  fileTypes: PropTypes.arrayOf(PropTypes.string),
  sources: PropTypes.arrayOf(PropTypes.oneOf(['webcam', 'facebook', 'instagram', 'dropbox', 'google-drive'])),
  withButton: PropTypes.bool,
  withFind: PropTypes.bool,
  withClearButton: PropTypes.bool,
  withoutMedia: PropTypes.bool,
  addButtonLabel: PropTypes$1.label,
  findButtonLabel: PropTypes$1.label,
  clearButtonLabel: PropTypes$1.label,
  allowMultipleUploads: PropTypes.bool,
  closeAfterFinish: PropTypes.bool,
  maxNumberOfFiles: PropTypes.number,
  namePath: PropTypes.string,
  thumbnailPath: PropTypes.string,
  sizePath: PropTypes.string,
  linkPath: PropTypes.string,
  uppyProps: PropTypes.shape({
    withUUID: PropTypes.bool
  }),
  width: PropTypes.number,
  height: PropTypes.number,
  disabled: PropTypes.bool,
  uploadDisabled: PropTypes.bool,
  outline: PropTypes.bool,
  loading: PropTypes.bool,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onClickAdd: PropTypes.func,
  onClickFind: PropTypes.func,
  className: PropTypes.string
};
var defaultProps$1 = {
  resource: 'medias',
  value: null,
  name: null,
  types: ['audio', 'image', 'video'],
  fileTypes: null,
  sources: ['webcam', 'facebook', 'instagram', 'dropbox', 'google-drive'],
  withButton: false,
  withFind: false,
  withClearButton: false,
  withoutMedia: false,
  addButtonLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "BeKVq6",
    defaultMessage: [{
      "type": 0,
      "value": "Upload file"
    }]
  }),
  findButtonLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "une5WQ",
    defaultMessage: [{
      "type": 0,
      "value": "Find a file"
    }]
  }),
  clearButtonLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "odaCUe",
    defaultMessage: [{
      "type": 0,
      "value": "Clear"
    }]
  }),
  allowMultipleUploads: false,
  closeAfterFinish: true,
  maxNumberOfFiles: 1,
  namePath: 'name',
  thumbnailPath: 'thumbnail_url',
  sizePath: 'metadata.size',
  linkPath: null,
  uppyProps: null,
  width: null,
  height: 300,
  disabled: false,
  uploadDisabled: false,
  outline: true,
  loading: false,
  onChange: null,
  onClear: null,
  onClickAdd: null,
  onClickFind: null,
  className: null
};
var UploadField = function UploadField(_ref) {
  var resource = _ref.resource,
    value = _ref.value,
    name = _ref.name,
    types = _ref.types,
    fileTypes = _ref.fileTypes,
    sources = _ref.sources,
    withButton = _ref.withButton,
    withFind = _ref.withFind,
    withClearButton = _ref.withClearButton,
    withoutMedia = _ref.withoutMedia,
    addButtonLabel = _ref.addButtonLabel,
    findButtonLabel = _ref.findButtonLabel,
    clearButtonLabel = _ref.clearButtonLabel,
    allowMultipleUploads = _ref.allowMultipleUploads,
    closeAfterFinish = _ref.closeAfterFinish,
    maxNumberOfFiles = _ref.maxNumberOfFiles,
    namePath = _ref.namePath,
    thumbnailPath = _ref.thumbnailPath,
    sizePath = _ref.sizePath,
    linkPath = _ref.linkPath,
    uppyProps = _ref.uppyProps,
    width = _ref.width,
    height = _ref.height,
    disabled = _ref.disabled,
    uploadDisabled = _ref.uploadDisabled,
    outline = _ref.outline,
    parentLoading = _ref.loading,
    onChange = _ref.onChange,
    onClear = _ref.onClear,
    onClickAdd = _ref.onClickAdd,
    onClickFind = _ref.onClickFind,
    className = _ref.className;
  var mergeData = useCallback(function (newValue) {
    // Merge the response from our back-end
    if (isObject(newValue) && isObject(newValue.response) && newValue.response.status === 200 && newValue.response.body !== null) {
      return _objectSpread(_objectSpread({}, newValue), newValue.response.body || null);
    }
    return newValue;
  }, []);
  var onComplete = useCallback(function (response) {
    // console.log('upload complete', response); // eslint-disable-line
    var newValue = null;
    if (isArray(response)) {
      if (allowMultipleUploads) {
        newValue = response;
      } else {
        var _response = _slicedToArray(response, 1),
          first = _response[0];
        newValue = first;
      }
    } else if (response && response.successful) {
      newValue = response.successful.length > 0 ? response.successful[0].response.body : null;
    }
    if (isArray(newValue)) {
      newValue = newValue.map(function (val) {
        return mergeData(val);
      });
    } else {
      newValue = mergeData(newValue);
    }

    // console.log('new upload value', newValue); // eslint-disable-line

    if (onChange !== null) {
      onChange(newValue);
    }
  }, [onChange, allowMultipleUploads, mergeData]);
  var typesString = useMemo(function () {
    return types !== null ? types.join('.') : ['audio', 'image', 'video'].join('.');
  }, [types]);
  var allowedFileTypes = useMemo(function () {
    if (fileTypes !== null) {
      return fileTypes;
    }
    return typesString.split('.').map(function (type) {
      return "".concat(type, "/*");
    });
  }, [typesString, fileTypes]);
  var uppyFinalProps = useMemo(function () {
    return _objectSpread(_objectSpread({
      maxNumberOfFiles: allowMultipleUploads && maxNumberOfFiles === 1 ? 50 : maxNumberOfFiles,
      allowedFileTypes: allowedFileTypes,
      sources: sources
    }, uppyProps), {}, {
      allowMultipleUploads: allowMultipleUploads,
      autoProceed: true,
      onComplete: onComplete
    });
  }, [uppyProps, allowedFileTypes, allowMultipleUploads, maxNumberOfFiles, sources, onComplete]);
  var uppy = useUppy(uppyFinalProps);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    modalOpened = _useState2[0],
    setModalOpened = _useState2[1];
  var openModal = useCallback(function () {
    setModalOpened(!modalOpened);
  }, [modalOpened, setModalOpened]);
  var closeModal = useCallback(function () {
    setModalOpened(false);
    if (uppy !== null) {
      uppy.cancelAll({
        reason: 'user'
      });
    }
  }, [uppy, setModalOpened]);
  var onClickClear = useCallback(function () {
    if (onClear !== null) {
      onClear();
    }
    if (uppy !== null) {
      uppy.cancelAll({
        reason: 'user'
      });
    }
  }, [uppy, onClear]);
  var onClickRemove = useCallback(function (idx) {
    if (onChange !== null && isArray(value) && value.length > 1) {
      onChange(value.filter(function (v, i) {
        return i !== idx;
      }));
    } else if (onChange !== null) {
      onChange(null);
    }
  }, [value, onChange]);
  var values = useMemo(function () {
    if (isArray(value)) {
      return value;
    }
    return value !== null ? [value] : null;
  }, [value]);

  // Resource-modal-picker
  var hasMedia = values !== null && values.length > 0;
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    resourceModalOpen = _useState4[0],
    setResourceModalOpen = _useState4[1];
  var showResourceModal = resource !== null && withFind && resourceModalOpen;
  var openResourceModal = useCallback(function () {
    setResourceModalOpen(true);
  }, [resourceModalOpen, setResourceModalOpen]);
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    modalItems = _useState6[0],
    setModalItems = _useState6[1];
  var closeResourceModal = useCallback(function () {
    setResourceModalOpen(false);
    setModalItems(null);
  }, [resourceModalOpen, setResourceModalOpen, setModalItems]);
  var finalOnClickFind = useCallback(function () {
    if (onClickFind !== null) {
      onClickFind();
    } else {
      openResourceModal();
    }
  }, [onClickFind, openResourceModal]);
  var onSelectionChange = useCallback(function (newValue) {
    if (allowMultipleUploads) {
      if (newValue !== null && !isArray(newValue)) {
        var _ref2 = newValue || {},
          _ref2$id = _ref2.id,
          id = _ref2$id === void 0 ? null : _ref2$id;
        if (id !== null) {
          var previous = (modalItems || []).find(function () {
            var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
              _ref3$id = _ref3.id,
              itemId = _ref3$id === void 0 ? null : _ref3$id;
            return id === itemId;
          });
          if (previous) {
            setModalItems((modalItems || []).filter(function () {
              var _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
                _ref4$id = _ref4.id,
                itemId = _ref4$id === void 0 ? null : _ref4$id;
              return id !== itemId;
            }));
          } else {
            setModalItems([].concat(_toConsumableArray(modalItems || []), [newValue]));
          }
        }
      } else if (newValue !== null && isArray(newValue)) {
        setModalItems(newValue);
      }
    } else {
      setModalItems(newValue);
    }
  }, [onChange, setResourceModalOpen, allowMultipleUploads, modalItems, setModalItems]);
  var confirmResourceModal = useCallback(function () {
    if (onChange !== null) {
      // Always multiple onchange
      onChange(modalItems);
      setResourceModalOpen(false);
      setModalItems(null);
    }
  }, [onChange, setResourceModalOpen, modalItems, allowMultipleUploads, setModalItems]);
  var initialQuery = useMemo(function () {
    return {
      types: types
    };
  }, [types]);
  var _useQuery = useQuery(initialQuery, true),
    listQuery = _useQuery.query,
    onListPageChange = _useQuery.onPageChange,
    onListQueryChange = _useQuery.onQueryChange,
    onListQueryReset = _useQuery.onQueryReset;
  var containerRef = useRef(null);

  // Keep this stable, uppy doesnt like
  var _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    finalUppy = _useState8[0],
    setFinalUppy = _useState8[1];
  useEffect(function () {
    if (uppy !== null && finalUppy === null) {
      setFinalUppy(uppy);
    }
  }, [uppy, finalUppy]);

  // Uppy state
  var _useState9 = useState(false),
    _useState10 = _slicedToArray(_useState9, 2),
    loading = _useState10[0],
    setLoading = _useState10[1];
  var startLoading = useCallback(function () {
    setLoading(true);
  }, []);
  var endLoading = useCallback(function () {
    setLoading(false);
  }, []);
  useEffect(function () {
    if (uppy !== null) {
      uppy.on('upload', startLoading);
      uppy.on('complete', endLoading);
      uppy.on('upload-error', endLoading);
      uppy.on('error', endLoading);
      uppy.on('cancell-all', endLoading);
    }
    return function () {
      if (uppy !== null) {
        uppy.off('upload', startLoading);
        uppy.off('complete', endLoading);
        uppy.off('upload-error', endLoading);
        uppy.off('error', endLoading);
        uppy.off('cancell-all', endLoading);
      }
    };
  }, [uppy, startLoading, endLoading]);
  var finalLoading = loading || parentLoading;
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty({}, className, className !== null)]),
    ref: containerRef
  }, !withoutMedia && hasMedia ? /*#__PURE__*/React.createElement(MediaCards, {
    value: values,
    namePath: namePath,
    thumbnailPath: thumbnailPath,
    sizePath: sizePath,
    linkPath: linkPath,
    disabled: disabled,
    onClickRemove: onClickRemove
  }) : null, !withoutMedia && hasMedia && withClearButton ? /*#__PURE__*/React.createElement("div", {
    className: "row mt-2"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-auto"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    theme: "primary",
    onClick: onClickClear
  }, /*#__PURE__*/React.createElement(Label, null, clearButtonLabel)))) : null, withoutMedia || (!hasMedia || allowMultipleUploads) && withButton ? /*#__PURE__*/React.createElement("div", {
    className: "row"
  }, /*#__PURE__*/React.createElement("div", {
    className: "col-auto"
  }, /*#__PURE__*/React.createElement(Button, {
    id: "trigger-uppy",
    type: "button",
    theme: "primary",
    icon: finalLoading ? 'loading' : 'upload',
    iconPosition: "right",
    onClick: onClickAdd || openModal,
    disabled: finalLoading || disabled,
    outline: outline
  }, /*#__PURE__*/React.createElement(Label, null, finalLoading ? /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "NozDYd",
    defaultMessage: [{
      "type": 0,
      "value": "Uploading"
    }]
  }) : addButtonLabel))), withFind ? /*#__PURE__*/React.createElement("div", {
    className: "col-auto ps-0"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    theme: "primary",
    icon: "search",
    iconPosition: "right",
    onClick: finalOnClickFind,
    disabled: disabled,
    outline: outline
  }, /*#__PURE__*/React.createElement(Label, null, findButtonLabel))) : null) : null, !uploadDisabled && !hasMedia && !withButton && finalUppy !== null ? /*#__PURE__*/React.createElement("div", {
    className: styles.dashboard
  }, /*#__PURE__*/React.createElement(Dashboard, Object.assign({
    uppy: finalUppy
    // {...(containerWidth !== null && height !== null
    //     ? { width: containerWidth }
    // : null)}
  }, width !== null ? {
    width: width
  } : null, height !== null ? {
    height: height
  } : null, {
    plugins: sources,
    inline: true,
    showProgressDetails: true,
    areInsidesReadyToBeVisible: true,
    proudlyDisplayPoweredByUppy: false
  }))) : null, !showResourceModal && !uploadDisabled && withButton && finalUppy !== null && modalOpened ? /*#__PURE__*/React.createElement(DashboardModal, {
    uppy: finalUppy,
    className: styles.dashboardModal,
    plugins: sources,
    open: true,
    onRequestClose: closeModal,
    proudlyDisplayPoweredByUppy: false,
    closeModalOnClickOutside: true,
    areInsidesReadyToBeVisible: true,
    isDashboardVisible: true,
    showProgressDetails: true,
    showAddFilesPanel: true,
    doneButtonHandler: closeModal,
    closeAfterFinish: closeAfterFinish
  }) : null, showResourceModal ? /*#__PURE__*/React.createElement(ModalResourceItems, {
    id: "upload-".concat(name),
    resource: resource,
    query: listQuery,
    onPageChange: onListPageChange,
    onQueryChange: onListQueryChange,
    onQueryReset: onListQueryReset,
    baseUrl: null,
    showActions: false,
    selectable: true,
    selectedItems: modalItems,
    onSelectionChange: onSelectionChange,
    multipleSelection: allowMultipleUploads,
    onClose: closeResourceModal
  }, /*#__PURE__*/React.createElement("div", {
    className: "d-flex mt-4 justify-content-between"
  }, modalItems !== null && modalItems.length > 0 ? /*#__PURE__*/React.createElement("span", {
    className: "me-2"
  }, modalItems.length, " items") : /*#__PURE__*/React.createElement("span", null), /*#__PURE__*/React.createElement("div", {
    className: "d-flex"
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    theme: "secondary",
    onClick: closeResourceModal,
    disabled: disabled,
    className: "d-block me-2"
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "PyxZY2",
    defaultMessage: [{
      "type": 0,
      "value": "Cancel"
    }]
  })), /*#__PURE__*/React.createElement(Button, {
    type: "button",
    theme: "primary",
    onClick: confirmResourceModal,
    disabled: disabled || modalItems !== null && modalItems.length === 0,
    className: "d-block"
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "rvOVCV",
    defaultMessage: [{
      "type": 0,
      "value": "Confirm selection"
    }]
  }))))) : null);
};
UploadField.propTypes = propTypes$1;
UploadField.defaultProps = defaultProps$1;

var definition = [{
  id: 'upload',
  component: 'Upload'
}, {
  id: 'audio',
  component: 'Audio'
}, {
  id: 'image',
  component: 'Image'
}, {
  id: 'video',
  component: 'Video'
}, {
  id: 'document',
  component: 'Document'
}, {
  id: 'font',
  component: 'Font'
}];

/* eslint-disable react/jsx-props-no-spreading */
var AudioField = function AudioField(props) {
  var types = useMemo(function () {
    return ['audio'];
  }, []);
  return /*#__PURE__*/React.createElement(UploadField, Object.assign({}, props, {
    types: types
  }));
};

/* eslint-disable react/jsx-props-no-spreading */
var ImageField = function ImageField(props) {
  var types = useMemo(function () {
    return ['image'];
  }, []);
  return /*#__PURE__*/React.createElement(UploadField, Object.assign({}, props, {
    types: types
  }));
};

/* eslint-disable react/jsx-props-no-spreading */
var ImagesField = function ImagesField(props) {
  var types = useMemo(function () {
    return ['image'];
  }, []);
  return /*#__PURE__*/React.createElement(UploadField, Object.assign({}, props, {
    types: types,
    allowMultipleUploads: true
  }));
};

/* eslint-disable react/jsx-props-no-spreading */
var VideoField = function VideoField(props) {
  var types = useMemo(function () {
    return ['video'];
  }, []);
  return /*#__PURE__*/React.createElement(UploadField, Object.assign({}, props, {
    types: types
  }));
};

/* eslint-disable react/jsx-props-no-spreading */
var DocumentField = function DocumentField(props) {
  var fileTypes = useMemo(function () {
    return ['.pdf'];
  }, []);
  var types = useMemo(function () {
    return ['document'];
  }, []);
  return /*#__PURE__*/React.createElement(UploadField, Object.assign({}, props, {
    fileTypes: fileTypes,
    types: types
  }));
};

/* eslint-disable react/jsx-props-no-spreading */
var FontField = function FontField(props) {
  var fileTypes = useMemo(function () {
    return ['.ttf', '.otf'];
  }, []);
  return /*#__PURE__*/React.createElement(UploadField, Object.assign({}, props, {
    fileTypes: fileTypes
  }));
};

var _excluded = ["value", "onChange"];
var propTypes = {
  value: PropTypes.shape({
    published: PropTypes.bool,
    featured: PropTypes.bool,
    created_at: PropTypes.string,
    updated_at: PropTypes.string
  }),
  onChange: PropTypes.func,
  types: PropTypes.arrayOf(PropTypes.string),
  fileTypes: PropTypes.arrayOf(PropTypes.string)
};
var defaultProps = {
  value: null,
  onChange: null,
  types: null,
  fileTypes: null
};
var UpdateFileField = function UpdateFileField(_ref) {
  var value = _ref.value;
    _ref.onChange;
    var props = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    newValue = _useState2[0],
    setNewValue = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    visibleFile = _useState4[0],
    setVisibleFile = _useState4[1];
  var _ref2 = value || {},
    previousId = _ref2.id,
    _ref2$type = _ref2.type,
    type = _ref2$type === void 0 ? null : _ref2$type;
  var onUploadChange = useCallback(function () {
    var uploadedFile = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    if (uploadedFile !== null) {
      setVisibleFile(uploadedFile);
    }
    setNewValue(uploadedFile);
  }, [setNewValue, setVisibleFile]);
  var uppyProps = useMemo(function () {
    return {
      meta: {
        previousId: previousId
      }
    };
  }, [previousId]);
  var uploadProps = useMemo(function () {
    if (type === 'image') {
      return {
        types: ['image']
      };
    }
    if (type === 'audio') {
      return {
        types: ['audio']
      };
    }
    if (type === 'video') {
      return {
        types: ['video']
      };
    }
    if (type === 'document') {
      return {
        types: [],
        fileTypes: ['.pdf']
      };
    }
    return null;
  }, [type]);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "mb-3"
  }, /*#__PURE__*/React.createElement(MediaCard, {
    value: visibleFile || value,
    withoutDescription: true
  })), /*#__PURE__*/React.createElement("div", {
    className: "mb-3"
  }, /*#__PURE__*/React.createElement(UploadField, Object.assign({}, props, uploadProps, {
    addButtonLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "daHjZg",
      defaultMessage: [{
        "type": 0,
        "value": "Edit file"
      }]
    }),
    allowMultipleUploads: false,
    linkPath: "panneauUrl",
    onChange: onUploadChange,
    onClose: onUploadChange,
    maxNumberOfFiles: 1,
    value: newValue,
    withButton: true,
    uppyProps: uppyProps
  }))));
};
UpdateFileField.propTypes = propTypes;
UpdateFileField.defaultProps = defaultProps;

export { AudioField, DocumentField, FontField, ImageField, ImagesField, UpdateFileField, VideoField, UploadField as default, definition };
